import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pause, UserPlus, ListTodo } from "lucide-react";
import { api, useFetch } from "@/lib/api";

const PRIORITY_CFG = {
  critical: "bg-red-500/10 text-red-400 border-red-500/20",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  medium: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  low: "bg-green-500/10 text-green-400 border-green-500/20",
};
const STATUS_CFG = {
  pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  in_progress: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
  completed: "bg-green-500/10 text-green-400 border-green-500/20",
  failed: "bg-red-500/10 text-red-400 border-red-500/20",
  cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
};

function TaskBadge({ value, map }) {
  const cls = map[value] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20";
  return <Badge variant="outline" className={`${cls} text-[10px] font-mono uppercase tracking-wider rounded-sm px-2 py-0.5 border`}>{value}</Badge>;
}

export default function Tasks() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const { data: tasks, loading, refetch } = useFetch(() => api.getTasks({}));
  const { data: agents } = useFetch(() => api.getAgents({}));
  const [selected, setSelected] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const EMPTY = { title: "", description: "", priority: "medium", task_type: "general", assigned_to: "", due_local: "" };
  const [newTask, setNewTask] = useState(EMPTY);

  const filtered = (tasks || []).filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    return true;
  });

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const selectAll = () => setSelected(filtered.length === selected.length ? [] : filtered.map((t) => t.id));

  const handleCreate = async () => {
    if (!newTask.title.trim()) return;
    const { due_local, ...rest } = newTask;
    await api.createTask({ ...rest, due_date: due_local ? new Date(due_local).toISOString() : null });
    setNewTask(EMPTY);
    setCreateOpen(false);
    refetch();
  };

  const handleBulk = async (action) => {
    if (selected.length === 0) return;
    await api.bulkAction({ task_ids: selected, action });
    setSelected([]);
    refetch();
  };

  const formatDate = (iso) => {
    if (!iso) return "No due date";
    return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider">TASK MANAGEMENT</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} tasks{selected.length > 0 && ` / ${selected.length} selected`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="task-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-36 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="task-priority-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-neon-cyan/90 text-black hover:bg-neon-cyan font-mono text-xs uppercase tracking-wider" data-testid="create-task-btn">
                <Plus className="w-4 h-4 mr-1" /> New Task
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-panel border-white/10 sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-wider">CREATE NEW TASK</DialogTitle>
                <DialogDescription className="sr-only">Create a task and optionally set a due date</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Title</Label>
                  <Input value={newTask.title} onChange={(e) => setNewTask((p) => ({ ...p, title: e.target.value }))} className="mt-1 bg-white/5 border-white/10 rounded-sm" data-testid="task-title-input" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Description</Label>
                  <Input value={newTask.description} onChange={(e) => setNewTask((p) => ({ ...p, description: e.target.value }))} className="mt-1 bg-white/5 border-white/10 rounded-sm" data-testid="task-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Priority</Label>
                    <Select value={newTask.priority} onValueChange={(v) => setNewTask((p) => ({ ...p, priority: v }))}>
                      <SelectTrigger className="mt-1 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Type</Label>
                    <Select value={newTask.task_type} onValueChange={(v) => setNewTask((p) => ({ ...p, task_type: v }))}>
                      <SelectTrigger className="mt-1 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="task-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Data Processing", "Content Generation", "Analysis", "Monitoring", "Classification", "Extraction", "Synthesis", "Report Generation"].map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Due date (optional)</Label>
                  <Input type="datetime-local" value={newTask.due_local} onChange={(e) => setNewTask((p) => ({ ...p, due_local: e.target.value }))} className="mt-1 bg-white/5 border-white/10 rounded-sm [color-scheme:dark]" data-testid="task-due-input" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Assign To</Label>
                  <Select value={newTask.assigned_to} onValueChange={(v) => setNewTask((p) => ({ ...p, assigned_to: v }))}>
                    <SelectTrigger className="mt-1 bg-white/5 border-white/10 rounded-sm text-sm" data-testid="task-assign-select"><SelectValue placeholder="Select agent" /></SelectTrigger>
                    <SelectContent>
                      {(agents || []).filter((a) => a.status !== "offline").map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="ghost" className="rounded-sm font-mono text-xs uppercase">Cancel</Button></DialogClose>
                <Button onClick={handleCreate} className="rounded-sm bg-neon-cyan/90 text-black hover:bg-neon-cyan font-mono text-xs uppercase tracking-wider" data-testid="submit-task-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {selected.length > 0 && (
        <Card className="glass-panel border-neon-cyan/20">
          <CardContent className="p-3 flex items-center gap-3">
            <span className="text-xs font-mono text-muted-foreground">{selected.length} selected</span>
            <Button size="sm" variant="ghost" onClick={() => handleBulk("pause")} className="text-xs font-mono uppercase" data-testid="bulk-pause-btn"><Pause className="w-3.5 h-3.5 mr-1" /> Pause</Button>
            <Button size="sm" variant="ghost" onClick={() => handleBulk("cancel")} className="text-xs font-mono uppercase text-red-400" data-testid="bulk-cancel-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected([])} className="text-xs font-mono uppercase ml-auto">Clear</Button>
          </CardContent>
        </Card>
      )}

      <Card className="glass-panel hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="w-10"><Checkbox checked={filtered.length > 0 && selected.length === filtered.length} onCheckedChange={selectAll} data-testid="select-all-tasks" /></TableHead>
                  <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Title</TableHead>
                  <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Status</TableHead>
                  <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Priority</TableHead>
                  <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Assigned To</TableHead>
                  <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Type</TableHead>
                  <TableHead className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i} className="border-white/5">
                    <TableCell colSpan={7}><div className="h-8 skeleton-shimmer rounded-sm" /></TableCell>
                  </TableRow>
                ))}
                {!loading && filtered.map((task) => (
                  <TableRow key={task.id} className="border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`task-row-${task.id}`}>
                    <TableCell><Checkbox checked={selected.includes(task.id)} onCheckedChange={() => toggleSelect(task.id)} data-testid={`task-check-${task.id}`} /></TableCell>
                    <TableCell className="font-medium text-sm max-w-[200px] truncate">{task.title}</TableCell>
                    <TableCell><TaskBadge value={task.status} map={STATUS_CFG} /></TableCell>
                    <TableCell><TaskBadge value={task.priority} map={PRIORITY_CFG} /></TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{task.assigned_to_name || "-"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{task.task_type}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{formatDate(task.due_date)}</TableCell>
                  </TableRow>
                ))}
                {!loading && filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                      <ListTodo className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="font-mono text-sm">No tasks found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading && [1, 2, 3, 4, 5].map((i) => <div key={i} className="h-24 rounded-sm glass-panel skeleton-shimmer" />)}
        {!loading && filtered.map((task) => (
          <Card key={task.id} className="glass-panel" data-testid={`task-mobile-${task.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex gap-1.5 flex-wrap">
                  <TaskBadge value={task.status} map={STATUS_CFG} />
                  <TaskBadge value={task.priority} map={PRIORITY_CFG} />
                </div>
                <Checkbox checked={selected.includes(task.id)} onCheckedChange={() => toggleSelect(task.id)} className="mt-0.5" />
              </div>
              <h3 className="text-sm font-medium leading-snug">{task.title}</h3>
              <div className="flex items-center justify-between mt-2 text-[10px] font-mono text-muted-foreground">
                <span>{task.assigned_to_name || "Unassigned"}</span>
                <span>{task.task_type}</span>
                <span>{formatDate(task.due_date)}</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <ListTodo className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-mono text-sm">No tasks found</p>
          </div>
        )}
      </div>
    </div>
  );
}
